import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi, chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * Documentação interna (`/admin/docs`): dado real, da API
 * (`apps/api/src/modulos/documentacao`, rotas `bff/v1/admin/documentacao/artigos`).
 * Toda regra — versão a cada salvamento, concorrência, árvore de dois níveis —
 * é conferida no banco (migração 0350).
 *
 * Os nomes dos campos seguem os que os componentes da tela já usam
 * (`parent_id`, `conteudo_markdown`, `updated_at`); a conversão do que a API
 * devolve acontece só aqui.
 */
export const CAMINHO_DA_API = '/bff/v1/admin/documentacao/artigos';

/** Linha da barra lateral: o artigo sem o texto. */
export interface DocArticle {
  id: string;
  parent_id: string | null;
  titulo: string;
  slug: string;
  icone: string;
  ordem: number;
  /** Versão vigente. Toda alteração informa a que a pessoa viu. */
  versao: number;
  updated_at: string;
}

/** O artigo aberto, com o texto. */
export interface DocArticleCompleto extends DocArticle {
  conteudo_markdown: string;
  created_at: string;
}

interface ArtigoDaApi {
  id: string;
  parentId: string | null;
  titulo: string;
  slug: string;
  icone: string;
  ordem: number;
  versao: number;
  atualizadoEm: string;
}

interface ArtigoCompletoDaApi extends ArtigoDaApi {
  conteudoMarkdown: string;
  criadoEm: string;
}

interface ListaDaApi {
  itens: ArtigoDaApi[];
  total: number;
  pagina: number;
  tamanho: number;
}

const TAMANHO_DA_PAGINA = 200;

export const CHAVES = {
  tudo: ['admin', 'documentacao'] as const,
  indice: ['admin', 'documentacao', 'indice'] as const,
  artigo: (id: string) => ['admin', 'documentacao', 'artigo', id] as const,
  versoes: (id: string) => ['admin', 'documentacao', 'versoes', id] as const,
};

function resumoDe(a: ArtigoDaApi): DocArticle {
  return {
    id: a.id,
    parent_id: a.parentId,
    titulo: a.titulo,
    slug: a.slug,
    icone: a.icone,
    ordem: a.ordem,
    versao: a.versao,
    updated_at: a.atualizadoEm,
  };
}

function completoDe(a: ArtigoCompletoDaApi): DocArticleCompleto {
  return { ...resumoDe(a), conteudo_markdown: a.conteudoMarkdown, created_at: a.criadoEm };
}

/** A barra lateral precisa da árvore inteira: junta as páginas do servidor. */
async function carregarIndice(): Promise<DocArticle[]> {
  const itens: DocArticle[] = [];
  for (let pagina = 1; ; pagina += 1) {
    const r = await chamarApi<ListaDaApi>(
      `${CAMINHO_DA_API}?pagina=${pagina}&tamanho=${TAMANHO_DA_PAGINA}`,
    );
    itens.push(...r.itens.map(resumoDe));
    if (r.itens.length === 0 || itens.length >= r.total) break;
  }
  return itens;
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}

/** Artigo que não existe não passa a existir na segunda tentativa. */
function naoInsistirSeNaoExiste(tentativas: number, erro: unknown): boolean {
  return (
    !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro)
  );
}

export function useDocs() {
  const cliente = useQueryClient();

  const articles = useQuery({
    queryKey: CHAVES.indice,
    queryFn: carregarIndice,
    retry: naoInsistirEmRecusa,
  });

  /** Toda escrita devolve o artigo atualizado: o detalhe é gravado e a lista, renovada. */
  function aoGravar(artigo: ArtigoCompletoDaApi) {
    cliente.setQueryData(CHAVES.artigo(artigo.id), completoDe(artigo));
    // A barra lateral já mostra o artigo gravado, sem esperar a renovação da lista.
    cliente.setQueryData<DocArticle[]>(CHAVES.indice, (lista) => {
      if (!lista) return lista;
      const resumo = resumoDe(artigo);
      return lista.some((a) => a.id === resumo.id)
        ? lista.map((a) => (a.id === resumo.id ? resumo : a))
        : [...lista, resumo];
    });
    void cliente.invalidateQueries({ queryKey: CHAVES.indice });
    void cliente.invalidateQueries({ queryKey: CHAVES.versoes(artigo.id) });
  }

  const updateArticle = useMutation({
    mutationFn: (dados: { id: string; versao: number; conteudo_markdown: string }) =>
      chamarApi<ArtigoCompletoDaApi>(`${CAMINHO_DA_API}/${dados.id}`, {
        metodo: 'PUT',
        corpo: { versao: dados.versao, conteudoMarkdown: dados.conteudo_markdown },
      }),
    onSuccess: aoGravar,
  });

  const restoreVersion = useMutation({
    mutationFn: (dados: { id: string; versao: number; numero: number }) =>
      chamarApi<ArtigoCompletoDaApi>(`${CAMINHO_DA_API}/${dados.id}/restaurar`, {
        metodo: 'POST',
        corpo: { versao: dados.versao, numero: dados.numero },
      }),
    onSuccess: aoGravar,
  });

  const createArticle = useMutation({
    mutationFn: (artigo: {
      titulo: string;
      slug: string;
      parent_id?: string | null;
      icone?: string;
      ordem?: number;
      conteudo_markdown?: string;
    }) =>
      chamarApi<ArtigoCompletoDaApi>(CAMINHO_DA_API, {
        metodo: 'POST',
        corpo: {
          titulo: artigo.titulo,
          slug: artigo.slug,
          parentId: artigo.parent_id ?? null,
          icone: artigo.icone,
          ordem: artigo.ordem,
          conteudoMarkdown: artigo.conteudo_markdown,
        },
      }),
    onSuccess: aoGravar,
  });

  const deleteArticle = useMutation({
    mutationFn: (dados: { id: string; versao: number }) =>
      chamarApi<void>(`${CAMINHO_DA_API}/${dados.id}?versao=${dados.versao}`, {
        metodo: 'DELETE',
      }),
    onSuccess: (_resposta, dados) => {
      cliente.setQueryData<DocArticle[]>(CHAVES.indice, (lista) =>
        lista?.filter((a) => a.id !== dados.id),
      );
      cliente.removeQueries({ queryKey: CHAVES.artigo(dados.id) });
      cliente.removeQueries({ queryKey: CHAVES.versoes(dados.id) });
      void cliente.invalidateQueries({ queryKey: CHAVES.indice });
    },
  });

  return {
    articles: articles.data ?? [],
    loading: articles.isLoading,
    error: articles.error,
    refetch: articles.refetch,
    updateArticle,
    restoreVersion,
    createArticle,
    deleteArticle,
  };
}

/** O artigo aberto, com o texto. Sem `id`, não consulta. */
export function useDocArticle(id: string | null) {
  const artigo = useQuery({
    queryKey: CHAVES.artigo(id ?? ''),
    queryFn: async () =>
      completoDe(await chamarApi<ArtigoCompletoDaApi>(`${CAMINHO_DA_API}/${id}`)),
    enabled: !!id,
    // Quem edita não pode ter o texto trocado debaixo dos dedos por uma
    // atualização de fundo: a versão que vale é a da abertura do editor.
    refetchOnWindowFocus: false,
    retry: naoInsistirSeNaoExiste,
  });

  return {
    article: artigo.data ?? null,
    loading: artigo.isLoading,
    error: artigo.error,
    refetch: artigo.refetch,
  };
}
